<template>
  <Splitter class="h-full w-full max-h-full" :gutter-size="2">
    <SplitterPanel
      :min-size="10"
      :size="isMobile ? 35 : 20"
      class="max-h-full p-2"
    >
      <Tree
        v-model:selectionKeys="selectedKeysReadOnly"
        :value="queryStore.joinStack.data"
        :filter="true"
        filter-mode="strict"
        selection-mode="multiple"
        :expanded-keys="expandedKeys.data"
        class="w-full h-full max-h-full overflow-auto !p-0 right"
        scrollable
        scroll-height="flex"
        :pt:pc-filter-input:root="
          ({ state }) => filterValueChanged(state.d_value)
        "
      >
        <template #default="slotProps">
          <!-- override insert the click event-->
          <div
            :draggable="slotProps.node.treeNodeType === 'column'"
            class="w-full !p-3"
            @click="queryStore.addQueryCol(slotProps.node.key)"
            @dragstart="
              styleDropZones();
              startDragQueryCol($event, slotProps.node.key);
            "
            @dragend="unstyleDropZones"
          >
            {{ slotProps.node?.label }}
          </div>
        </template>
      </Tree>
    </SplitterPanel>
    <SplitterPanel :size="isMobile ? 65 : 80">
      <Splitter
        class="h-full w-full"
        :gutter-size="2"
        :layout="isMobile ? 'vertical' : 'horizontal'"
      >
        <SplitterPanel
          v-for="(col, index) in queryColTypes"
          :key="index"
          class="w-full h-full"
          :size="col.size"
        >
          <QueryColPanel
            :list-type="col.type"
            @start-chip-drag="styleDropZones"
            @stop-chip-drag="unstyleDropZones"
            @open-filter-dialogue="openDialogueVisible = true"
          />
        </SplitterPanel>
      </Splitter>
      <PopulationLoad
        v-model:visible="openDialogueVisible"
        v-model:loading="populationLoading"
        v-model:populations="populationsInfo"
        @open-population="queryStore.openPopulation(data)"
        @show="getPopulationsInfo"
      />
    </SplitterPanel>
  </Splitter>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import Splitter from "primevue/splitter";
import SplitterPanel from "primevue/splitterpanel";
import Tree from "primevue/tree";
import QueryColPanel from "@/components/information_tabs/query_elements/QueryColPanel.vue";
import { useQueryStore } from "@/stores/queryStore.js";
import PopulationLoad from "@/components/information_tabs/PopulationLoad.vue";
import axiosRepo from "@/repositories/AxiosRepository";
import { useMediaQuery } from "@vueuse/core";

const isMobile = useMediaQuery("(max-width: 768px)");

const queryStore = useQueryStore();

const populationsInfo = ref([]);
const populationLoading = ref(false);

const expandedKeys = reactive({ data: {} });
const searchLabel = ref("");
const openDialogueVisible = ref(false);

const queryColTypes = [
  { type: "keep", size: isMobile.value ? 30 : 20 },
  { type: "measure", size: isMobile.value ? 30 : 30 },
  { type: "filter", size: isMobile.value ? 40 : 50 },
];

async function getPopulationsInfo() {
  try {
    populationLoading.value = true;
    const response = await axiosRepo.get_populations();
    populationsInfo.value = response.data;
  } catch (err) {
    console.error(err);
  } finally {
    populationLoading.value = false;
  }
}

function startDragQueryCol(evt, key) {
  evt.dataTransfer.dropEffect = "copy";
  evt.dataTransfer.effectAllowed = "copy";
  evt.dataTransfer.setData("colKey", key);
  console.debug("begin drag of QueryCol node", key);
}

function styleDropZones() {
  const els = document.querySelectorAll(".drop-zone");
  els.forEach((e) => e.setAttribute("drop-active", "true"));
}

function unstyleDropZones() {
  const els = document.querySelectorAll(".drop-zone");
  els.forEach((e) => e.setAttribute("drop-active", "false"));
}

function filterValueChanged(value) {
  if (value && value?.trim().length >= 1) {
    // Expand all nodes when a new value is entered in the text field
    if (searchLabel.value !== value) {
      searchLabel.value = value;
      expandAll();
    }
    // Collapse all nodes when value is erased
  } else if (
    value != null &&
    value?.trim().length === 0 &&
    searchLabel.value.length !== 0
  ) {
    collapseAll();
    searchLabel.value = "";
  }
}

function expandNode(node) {
  if (node.children && node.children.length && node.key) {
    console.debug("expanding: ", node.key);
    expandedKeys.data[node.key] = true;
  }
}

function expandAll() {
  for (const node of queryStore.joinStack.data) {
    console.debug("trying to expand: ", node.key);
    expandNode(node);
  }
}

function collapseNode(node, currentExpandedSiteKey) {
  if (node.children && node.children.length && node.key) {
    delete currentExpandedSiteKey[node.key];
  }
}

function collapseAll() {
  const currentExpandedSiteKey = expandedKeys.data;
  for (const node of queryStore.joinStack.data) {
    collapseNode(node, currentExpandedSiteKey);
  }
  expandedKeys.data = currentExpandedSiteKey;
}

const selectedKeysReadOnly = computed({
  get() {
    let queryKeys = queryStore.queryCols.data.map((col) => col.key);
    console.debug("noticed selected keys change. keys", queryKeys);
    const selected = {};
    queryKeys.forEach((key) => {
      selected[key] = true;
    });
    return selected;
  },
  set() {},
});
</script>
